import React, { useContext } from "react"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { CarrotCardCostSharePreAuthContext } from "../../state/CarrotCardCostSharePreAuthContext"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { IntendedCarrierValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { SelectWithTileStep } from "#/components/carrot-card-cost-share/steps/variants/SelectWithTileStep"
import { ThisIsADifferentExpenseTypeBox } from "#/components/carrot-card-cost-share/steps/shared/ThisIsADifferentExpenseTypeBox"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"

const intendedCarrierOptions = [
  { label: defineMessage({ defaultMessage: "Me" }), value: IntendedCarrierValues.ME },
  {
    label: defineMessage({ defaultMessage: "My legal spouse or tax-dependent partner" }),
    value: IntendedCarrierValues.LEGAL_PARTNER
  },
  {
    label: defineMessage({ defaultMessage: "My non-married partner" }),
    value: IntendedCarrierValues.NON_MARRIED_PARTNER
  },
  { label: defineMessage({ defaultMessage: "Gestational carrier" }), value: IntendedCarrierValues.GESTATIONAL_CARRIER }
]

export function IntendedCarrierStep(): JSX.Element {
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { intendedCarrier },
    serverState: { hasGestationalCarrierCoverage }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const intl = useIntl()
  const theme = useTheme()

  const formattedOptions = useConvertTranslatedLabel(intendedCarrierOptions) as FormSelectOption[]

  function onSubmit(formValues: { [key: string]: string }): void {
    dispatchFormState({ type: "UPDATE_FIELD", field: "intendedCarrier", value: formValues.intendedCarrier })
    if (formValues.intendedCarrier === IntendedCarrierValues.GESTATIONAL_CARRIER && !hasGestationalCarrierCoverage) {
      send("GESTATIONAL_CARRIER_WITHOUT_COVERAGE")
      return
    }
    if (formValues.intendedCarrier === IntendedCarrierValues.GESTATIONAL_CARRIER && hasGestationalCarrierCoverage) {
      dispatchFormState({ type: "UPDATE_FIELD", field: "journeyType", value: JourneyTypes.GC })
    }
    send(formValues.intendedCarrier)
  }

  return (
    <SelectWithTileStep
      title={intl.formatMessage({ defaultMessage: "Who is the intended carrier?" })}
      description={
        <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <Typography>
            <FormattedMessage defaultMessage="The intended carrier receives the donor material and would carry a pregnancy resulting from treatment." />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="This helps us determine if the care may be a qualified medical expense." />
          </Typography>
        </Stack>
      }
      selectName="intendedCarrier"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={intendedCarrier}
    >
      <ThisIsADifferentExpenseTypeBox name="intendedCarrier" />
    </SelectWithTileStep>
  )
}
